/**
 * Resolves a tool result that the universal envelope offloaded to a workspace
 * file.
 *
 * Any tool result whose JSON exceeds the envelope's inline limit (4KB by
 * default) is written to `ws://.data/<tool>_<id>.json` and replaced with a
 * pointer: `{ ok, _dataRef, sizeBytes, head, tail, summary }`. The real payload
 * — rows, stdout, exit codes — is only in that file. A renderer that reads its
 * fields straight off the result therefore shows a successful call as empty,
 * which is what this hook exists to prevent.
 *
 * `.data/` is GCS-authoritative and served as text by the workspace preview
 * endpoint, so the payload can be fetched back on demand.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/services/api';
import { useThreadState } from '../AiChatProvider';

/** Fields the envelope adds when it offloads or truncates a payload. */
export interface EnvelopeFields {
  _dataRef?: string;
  sizeBytes?: number;
  head?: string;
  tail?: string;
  summary?: unknown;
  _truncated?: boolean;
  _reason?: string;
}

/** Default ceiling for loading an offloaded payload without a click. */
export const AUTO_FETCH_BYTES = 256_000;

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export interface OffloadedResult<T> {
  /** True when the payload lives in a workspace file rather than inline. */
  isOffloaded: boolean;
  /** The fetched payload, or null until it has been loaded. */
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Load the payload; safe to call repeatedly. */
  load: () => void;
  sizeBytes: number;
  /** The envelope's one-line preview, when it produced one. */
  summary?: string;
}

export function useOffloadedResult<T>(
  result: EnvelopeFields | undefined | null,
  autoFetchBytes: number = AUTO_FETCH_BYTES,
): OffloadedResult<T> {
  const { currentThreadId: threadId } = useThreadState();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const didAutoFetch = useRef(false);

  const dataRef = result?._dataRef;
  const sizeBytes = result?.sizeBytes ?? 0;
  const wsPath = (dataRef ?? '').replace(/^ws:\/\//, '');

  const load = useCallback(() => {
    if (!threadId || !wsPath) return;
    setLoading((busy) => {
      if (busy) return busy;
      void (async () => {
        setError(null);
        try {
          const res = await api.get<{ type: 'text' | 'binary'; content?: string }>(
            `/chat/threads/${threadId}/workspace/preview`,
            { path: wsPath },
          );
          if (res.type !== 'text' || typeof res.content !== 'string') {
            throw new Error('Offloaded result is not readable as text.');
          }
          setData(JSON.parse(res.content) as T);
        } catch (err: any) {
          setError(err?.message ?? 'Failed to load the offloaded result.');
        } finally {
          setLoading(false);
        }
      })();
      return true;
    });
  }, [threadId, wsPath]);

  useEffect(() => {
    if (didAutoFetch.current || !wsPath || data !== null) return;
    if (sizeBytes > 0 && sizeBytes <= autoFetchBytes) {
      didAutoFetch.current = true;
      load();
    }
  }, [wsPath, sizeBytes, autoFetchBytes, data, load]);

  return {
    isOffloaded: Boolean(dataRef),
    data,
    loading,
    error,
    load,
    sizeBytes,
    summary: typeof result?.summary === 'string' ? result.summary : undefined,
  };
}
